/**
 * The two faces of a rows-side `Sheet` its package hands out.
 *
 * - `SheetModelReads` — what a row's `sheetReads` and a renderer host's
 *   `platformOf(handle).sheet` / `platformOf(client).openSheet(key)` are: the
 *   sheet's reads (rows, tree, metadata, date-keyed and canonical cells, the
 *   formula graph as a read-only view, the header / footer surfaces). No
 *   writer of any kind. It is a frozen forwarding face (`sheetReadsOf(sheet)`,
 *   one per sheet), and every row it hands out is a frozen `ModelRowReads`
 *   face (`../rows/model-row-reads`), so neither a cast nor an `instanceof` reaches
 *   the `Sheet` or a live row behind it; an implementor takes the live row
 *   through `liveRowOf(row)` (`@tento-lona/sheets/internal-ui`, a package-private friend entry).
 * - `SheetModel` — what `SheetHandle.model` (`@tento/lona`) is: a frozen
 *   forwarding face over the sheet (`sheetModelOf(sheet)` — a separate
 *   object, so no cast reaches the `Sheet` behind it) carrying the reads plus
 *   one query an app asks of the rows-side model: the alias a structural
 *   move would fork (`aliasToFork`). It writes nothing: an app materializes a
 *   header / footer surface row with `handle.rows.ensureSurface(kind,
 *   actor)`, a checked write stamped with its actor. No cell peeks (`peekCells` /
 *   `peekCell`, the store's bare entries): an app reads a sheet's cached
 *   cells as addressable `SheetRow.DateCell`s through
 *   `handle.cells.peekInRange(range)`, the one cached cell read; a
 *   renderer's reads face keeps them. Not a renderer's copy-on-write display
 *   attribute: an app writes it through the SDK row that owns the option,
 *   with its actor (`row.envelopes.apply({ key, value }, actor)`). Not `detachAlias`: on a
 *   server-backed sheet the forked children keep client-minted ids the
 *   server does not share, so it is off the model — an app forks through
 *   the handle's own `handle.detachAlias(id)`, which takes a part-way
 *   failure back. No local-only writer: a template preview seeds and
 *   inserts a local-only row through the host door,
 *   `platformOf(handle).local.add(spec, actor)`; an app persists a row with
 *   `handle.rows.add(spec, actor)`.
 *
 * Neither carries the sheet's internals — stores, ingestion gate, promote
 * hook, backend, local-only tree / attribute / cell writes. The package
 * reaches the full `Sheet` through its friend module; the SDK through
 * `@tento-lona/sheets/internal-sdk`; a harness through `instrumentationOf`
 * (`@tento-lona/internal-testing`).
 *
 * Both are `Pick`s over `Sheet` (the row-returning reads re-typed to
 * `ModelRowReads`), so they cannot drift from the class: `Sheet` must stay
 * assignable to both (`SheetIsItsReads` below fails to compile otherwise), and
 * the member snapshot of the repository's hygiene suite pins them.
 */

import { InvalidationSink } from "@tento-core/invalidation";
import { type ModelRowReads, rowReadsOf } from "../rows/model-row-reads.ts";
import type { RowId } from "../ids/row-id.ts";
import { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelSheet } from "./rows-sheet.ts";
import type { ModelRow } from "../rows/model-row.ts";
import type { Option } from "../core/option.ts";

/** A rows-side sheet's reads — the type of `ModelRow.sheetReads`. */
export type SheetModelReads =
  & SheetModelReadMembers
  & SheetModelReads.Rows
  & {
    /**
     * The sheet's identity scope, resolve-only: the registry's frozen
     * `RowLocalId.resolverOf` face (no alias / claim / setCurrentUser, and
     * `instanceof RowLocalId.Registry` is false).
     */
    readonly registry: RowLocalId.Resolver;
    /** The sheet's invalidation stream, subscribe-only: the sink's frozen `InvalidationSink.sourceOf` face (no emit). */
    readonly invalidationSink: InvalidationSink.Source;
  };

/** The members `SheetModelReads` picks from `Sheet` as they are. */
export type SheetModelReadMembers = Pick<
  ModelSheet,
  | "networkKey"
  | "id"
  | "title"
  | "width"
  | "timescale"
  | "accessRole"
  | "ephemeral"
  | "editable"
  | "graph"
  | "formulaAmbiguities"
  | "rowMeta"
  | "parentOf"
  | "childrenOf"
  | "rootIds"
  | "peekCells"
  | "peekCell"
  | "peekCanonicalCell"
  | "subscribeCanonicalCells"
  | "aliasDisplayName"
  | "peekRendererDisplayAttr"
  | "registries"
  | "peekDataMirrorsOf"
>;

/** Types that go with `SheetModelReads`: `Rows`. */
export namespace SheetModelReads {
  /** The row-returning reads of `Sheet`, each handing out rows as their reads. */
  export type Rows = {
    /** The top-level rows (the sheet root's children), in tree order (a frozen array). */
    readonly rootRows: readonly ModelRowReads[];
    /** Every row, flattened: the top-level rows and all their descendants,
     *  breadth-first (`rootRows` is the first level only). */
    allRows(): readonly ModelRowReads[];
    /** The row with `id`, or unset. A `RowId` or wire key resolves through
     *  `registry` first. */
    peekRow(id: RowLocalId): Option<ModelRowReads>;
    /** The first row labelled `label`, or unset. */
    peekByLabel(label: string): Option<ModelRowReads>;
    /** The row with lookup key `key`, or unset. */
    peekByLookupKey(key: string): Option<ModelRowReads>;
    /** Every row with lookup key `key`. */
    peekAllByLookupKey(key: string): readonly ModelRowReads[];
    /** The header surface row, or unset. */
    peekHeaderRow(): Option<ModelRowReads>;
    /** The footer surface row, or unset. */
    peekFooterRow(): Option<ModelRowReads>;
  };
}

/** Compiles only while `T` is assignable to `U`: the faces stay `Sheet`'s and `ModelRow`'s own. */
type Assignable<T extends U, U> = T;
/** `Sheet` is its reads, and a live row is its row reads (the faces add nothing the class lacks). */
export type SheetIsItsReads = [Assignable<ModelSheet, SheetModelReads>, Assignable<ModelRow, ModelRowReads>];

/** Every reads face `sheetReadsOf` built, keyed by its sheet (one face per sheet). */
const readFaces = new WeakMap<ModelSheet, SheetModelReads>();

/** A row read off the sheet, handed out as its reads face; unset stays unset. */
function faceOf(row: Option<ModelRow> | undefined): Option<ModelRowReads> {
  return row == null ? null : rowReadsOf(row);
}

/**
 * The face a rows-side sheet's reads are handed out as — `row.sheetReads`,
 * `platformOf(handle).sheet`, `platformOf(client).openSheet(key)` /
 * `peekSheet(key)` / `ephemeralSheet(spec)`: a frozen object forwarding
 * exactly `SheetModelReads`' members to `sheet`, whose row-returning reads
 * hand out each row's frozen reads face (`rowReadsOf`) in a frozen array, and
 * whose `registry`, `graph` and `invalidationSink` are frozen read faces
 * (`RowLocalId.resolverOf`, `DependencyGraph.readsOf`,
 * `InvalidationSink.sourceOf`), never the live registry, graph or sink. Every read answers
 * from the live sheet, and nothing else of it is reachable — `reads as Sheet`
 * names an object with no writer, and `instanceof` finds no `Sheet` or
 * `ModelRow`. Built once per sheet: every door hands out the same object.
 */
export function sheetReadsOf(sheet: ModelSheet): SheetModelReads {
  const cached = readFaces.get(sheet);
  if (cached != null) return cached;
  const face: SheetModelReads = {
    get networkKey() {
      return sheet.networkKey;
    },
    get id() {
      return sheet.id;
    },
    get title() {
      return sheet.title;
    },
    get width() {
      return sheet.width;
    },
    get timescale() {
      return sheet.timescale;
    },
    get accessRole() {
      return sheet.accessRole;
    },
    get ephemeral() {
      return sheet.ephemeral;
    },
    get editable() {
      return sheet.editable;
    },
    get graph() {
      return sheet.graph;
    },
    get formulaAmbiguities() {
      return Object.freeze(sheet.formulaAmbiguities.map(item => Object.freeze({ ...item, candidates: Object.freeze([...item.candidates]) })));
    },
    get registries() {
      return sheet.registries;
    },
    get registry() {
      return RowLocalId.resolverOf(sheet.registry);
    },
    get invalidationSink() {
      return InvalidationSink.sourceOf(sheet.invalidationSink);
    },
    get rootRows() {
      return Object.freeze(sheet.rootRows.map(rowReadsOf));
    },
    rowMeta: (id) => sheet.rowMeta(id),
    parentOf: (id) => sheet.parentOf(id),
    childrenOf: (id) => Object.freeze([...sheet.childrenOf(id)]),
    rootIds: () => Object.freeze([...sheet.rootIds()]),
    peekCells: (id, range) => Object.freeze([...sheet.peekCells(id, range)]),
    peekCell: (id, date, cellId) => sheet.peekCell(id, date, cellId),
    peekCanonicalCell: (key, date, cellId) => sheet.peekCanonicalCell(key, date, cellId),
    subscribeCanonicalCells: (key, handler) => sheet.subscribeCanonicalCells(key, handler),
    aliasDisplayName: (id) => sheet.aliasDisplayName(id),
    peekRendererDisplayAttr: (sourceId, attr) => sheet.peekRendererDisplayAttr(sourceId, attr),
    peekDataMirrorsOf: (owner) => sheet.peekDataMirrorsOf(owner),
    allRows: () => Object.freeze(sheet.allRows().map(rowReadsOf)),
    peekRow: (id) => faceOf(sheet.peekRow(id)),
    peekByLabel: (label) => faceOf(sheet.peekByLabel(label)),
    peekByLookupKey: (key) => faceOf(sheet.peekByLookupKey(key)),
    peekAllByLookupKey: (key) => Object.freeze(sheet.peekAllByLookupKey(key).map(rowReadsOf)),
    peekHeaderRow: () => faceOf(sheet.peekHeaderRow()),
    peekFooterRow: () => faceOf(sheet.peekFooterRow()),
  };
  Object.freeze(face);
  readFaces.set(sheet, face);
  return face;
}

/** The reads `SheetModel` leaves out: the cell store's bare entries (see the module note). */
type SheetModelCellPeeks = "peekCells" | "peekCell";

/**
 * A rows-side sheet as an app holds it (`SheetHandle.model`): its reads —
 * the cell peeks excepted — plus the `aliasToFork` query (see the module
 * note). No writer.
 */
export type SheetModel =
  & Omit<SheetModelReads, "peekCells" | "peekCell">
  & Pick<ModelSheet, "aliasToFork">;

/**
 * The face `SheetHandle.model` hands out: a frozen object carrying the
 * sheet's reads face (`sheetReadsOf(sheet)` — the same getters, so every row
 * it hands out is that row's frozen `ModelRowReads` face, never a live
 * `ModelRow`) plus `SheetModel`'s `aliasToFork` query, forwarded to
 * `sheet`. Every read answers from the live sheet, and nothing else
 * of it is reachable — not through a cast (`model as Sheet` names an object
 * that has no writer), a bracket access, a prototype or a row it hands out
 * (`instanceof ModelRow` is false for each; an implementor takes the live row
 * through `liveRowOf`). Build it once per sheet and hand the same object out
 * (`SheetHandle.model` caches it). The SDK reaches it through
 * `@tento-lona/sheets/internal-sdk`.
 */
export function sheetModelOf(sheet: ModelSheet): SheetModel {
  const verbs: Pick<SheetModel, "aliasToFork"> = {
    aliasToFork: (id) => sheet.aliasToFork(id),
  };
  const face: SheetModel = withReadMembers(verbs, sheetReadsOf(sheet));
  return Object.freeze(face);
}

/** The cell peeks `withReadMembers` leaves off the model. */
const MODEL_OMITS: ReadonlySet<string> = new Set<SheetModelCellPeeks>(["peekCells", "peekCell"]);

/**
 * `verbs` with every member of `reads` but the cell peeks defined onto it —
 * its getters stay getters (a spread would snapshot them), so the model's
 * reads follow the live sheet exactly as the reads face does.
 */
function withReadMembers<V extends object>(verbs: V, reads: SheetModelReads): V & Omit<SheetModelReads, SheetModelCellPeeks>;
function withReadMembers(verbs: object, reads: object): object {
  const descriptors = Object.getOwnPropertyDescriptors(reads);
  const kept = Object.fromEntries(Object.entries(descriptors).filter(([name]) => !MODEL_OMITS.has(name)));
  return Object.defineProperties(verbs, kept);
}
